// A2-543 — the client's own exchange-rate method, per country whose law
// lets the client elect one (operator 2026-10-27: "of course build").
//
// Pick, don't write: one dropdown per country the client files in, the
// default first (today's behaviour), and a month picker for when the
// client adopted the method — Method 2 in the UK and Denmark's method bind
// from a date, and an earlier period must not pick up a later election.
// The choices and their conditions come from rule_data/fx_policy.json via
// GET /api/clients/fx-method-choices, so a country's law lives in one place.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFxMethodChoices } from "../../lib/api";

const RATE_LABELS = {
  OPERATOR: "The rate you enter (default)",
  ECB_LATEST_ON_TAX_POINT: "ECB rate of the invoice day, fetched automatically",
  HMRC_MONTHLY: "HMRC monthly rate (Method 2), fetched automatically",
};
const EURO = new Set(["AT", "ES", "IT"]);

const countriesOf = (form) => {
  const out = new Set();
  const est = (form.establishment_country || "").toUpperCase();
  if (est) out.add(est === "UK" ? "GB" : est);
  for (const r of form.company_vat_numbers || []) {
    if ((r.registration_type || "LOCAL").toUpperCase() !== "LOCAL") continue;
    const cc = (r.country_code || "").toUpperCase();
    if (cc) out.add(cc === "UK" ? "GB" : cc);
  }
  return out;
};

export const ClientFxMethods = ({ form, setFxMethods }) => {
  const { t } = useTranslation();
  const [choices, setChoices] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    getFxMethodChoices().then((c) => live && setChoices(c || {}))
      .catch(() => live && setErr(true));
    return () => { live = false; };
  }, []);

  if (err) {
    return (
      <p className="text-xs text-rose-700 dark:text-rose-300" data-testid="client-fx-methods-error">
        {t("ui.admin_clients.form.fx_methods.load_error", { defaultValue: "The exchange-rate choices could not be loaded. Reload the page to try again." })}
      </p>
    );
  }
  if (!choices) return null;
  const methods = form.fx_methods || {};
  const mine = [...countriesOf(form)].filter((cc) => choices[cc]).sort();
  if (!mine.length) {
    return (
      <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="client-fx-methods-none">
        {t("ui.admin_clients.form.fx_methods.none", { defaultValue: "None of this client's returns offers a choice of exchange-rate method." })}
      </p>
    );
  }
  const set = (cc, patch) => {
    const next = { ...methods, [cc]: { ...(methods[cc] || {}), ...patch } };
    if (!next[cc].rate || next[cc].rate === "OPERATOR") delete next[cc];
    setFxMethods(next);
  };
  return (
    <div className="space-y-4" data-testid="client-fx-methods">
      <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
        {t("ui.admin_clients.form.fx_methods.help", { defaultValue: "Each choice is the client's own, made under that country's law. A rate you enter on the calculation always wins over a fetched one." })}
      </p>
      {mine.map((cc) => {
        const c = choices[cc];
        const pick = methods[cc] || {};
        const rate = pick.rate || c.default;
        const chosen = c.choices.find((x) => x.rate === rate);
        return (
          <div key={cc} className="space-y-1" data-testid={`client-fx-method-${cc}`}>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200" htmlFor={`fx-method-${cc}`}>
              {cc} · <span className="font-normal">{c.provision}</span>
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <select
                id={`fx-method-${cc}`}
                data-testid={`client-fx-method-select-${cc}`}
                value={rate}
                onChange={(e) => set(cc, { rate: e.target.value })}
                className="w-full md:w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-3 py-2 text-sm rounded-md"
              >
                <option value={c.default}>
                  {t(`ui.admin_clients.form.fx_methods.rate.${c.default}`, { defaultValue: RATE_LABELS[c.default] || c.default })}
                </option>
                {c.choices.map((x) => (
                  <option key={x.rate} value={x.rate}>
                    {t(`ui.admin_clients.form.fx_methods.rate.${x.rate}`, { defaultValue: RATE_LABELS[x.rate] || x.rate })}
                  </option>
                ))}
              </select>
              {chosen && (
                <label className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-200">
                  {t("ui.admin_clients.form.fx_methods.from", { defaultValue: "Adopted from" })}
                  <input
                    type="month"
                    data-testid={`client-fx-method-from-${cc}`}
                    value={pick.from || ""}
                    onChange={(e) => set(cc, { from: e.target.value })}
                    className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-50 px-2 py-1 text-base sm:text-sm rounded-md"
                  />
                </label>
              )}
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
              {chosen ? chosen.note : c.default_note}
            </p>
            {chosen && !pick.from && (
              <p className="text-[11px] text-amber-700 dark:text-amber-300" data-testid={`client-fx-method-from-missing-${cc}`}>
                {t("ui.admin_clients.form.fx_methods.from_missing", { defaultValue: "Pick the month the client adopted it — the client can't be saved without it." })}
              </p>
            )}
            {chosen && EURO.has(cc) && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {t("ui.admin_clients.form.fx_methods.euro_shared", { defaultValue: "Euro returns share one rate per day: this applies only while every euro return the client files allows the ECB rate (Germany's does not, and Austria's, Italy's and Spain's only by election)." })}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ClientFxMethods;
